import {
  $createListItemNode,
  $createListNode,
  $isListItemNode,
  $isListNode,
  type ListItemNode,
  type ListNode,
} from "@lexical/list";
import { $isDecoratorNode, $isElementNode, type LexicalNode } from "lexical";

/** A block that a list item cannot hold: anything block-level that is not a nested list. */
const $isBlockInItem = (node: LexicalNode): boolean =>
  !$isListNode(node) &&
  (($isElementNode(node) && !node.isInline()) || ($isDecoratorNode(node) && !node.isInline()));

/**
 * Moves the blocks a list item was given out of it, to where they would have gone in a list that
 * is not nested: between two halves of the list, one level up.
 */
function $hoistBlocks(item: ListItemNode, list: ListNode): void {
  // Last block first: what trails a block then never holds another block, and may be wrapped in a
  // list item (which would take a paragraph's text and discard the paragraph).
  for (const child of item.getChildren().toReversed()) {
    if (!$isBlockInItem(child)) continue;
    const trailing = child.getNextSiblings();
    const following = item.getNextSiblings();
    list.insertAfter(child);
    if (trailing.length === 0 && following.length === 0) continue;
    const rest = $createListNode(list.getListType());
    if (trailing.length > 0) rest.append($createListItemNode().append(...trailing));
    rest.append(...following);
    child.insertAfter(rest);
  }
}

/** An item is its inline content, or the wrapper of exactly one nested list. Anything else is split. */
function $splitMixedItem(item: ListItemNode): void {
  const children = item.getChildren();
  if (children.length < 2 || !children.some($isListNode)) return;
  const runs: LexicalNode[][] = [];
  for (const child of children) {
    const run = runs.at(-1);
    if (run === undefined || $isListNode(child) || $isListNode(run[0])) runs.push([child]);
    else run.push(child);
  }
  let last = item;
  for (const run of runs.slice(1)) {
    const next = $createListItemNode().append(...run);
    last.insertAfter(next);
    last = next;
  }
}

/**
 * Puts the list items above `node` back into a shape the rest of Lexical handles, after an insert.
 *
 * Upstream's insert is right for a top-level item (`ListItemNode.insertAfter` splits the list
 * around a block) but only ever climbs one level. Into a nested item, pasted blocks are left inside
 * the item that wraps the nested list (`listitem > [list, paragraph]`), and a pasted list of
 * another type leaves that wrapper holding several lists. Neither is lost at once, but nothing in
 * Lexical expects them: a later list merge drops the paragraph with its "wrapper", and converting
 * one nested item drops the sibling lists. The property test found both as lost text.
 */
export function $repairListItemsAbove(node: LexicalNode): void {
  let item = node.getParent();
  while (item !== null) {
    const list = item.getParent();
    if (!$isListItemNode(item) || !$isListNode(list)) {
      item = list;
      continue;
    }
    const outer = list.getParent();
    if (item.getChildren().some($isBlockInItem)) $hoistBlocks(item, list);
    $splitMixedItem(item);
    if (item.isEmpty()) item.remove();
    if (list.isEmpty()) list.remove();
    // Hoisted blocks are now in the next item up, if the list is nested deeper still.
    item = outer;
  }
}
